import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import {
  resolveQueryTotalRowCount,
  resolveSortRowCount,
} from "../../../utils/sortLimitOptions";
import { EXECUTE_SQL_MAX_LIMIT } from "../../../utils/tablePaginationUtils";
import { sanitizeSortColumnName } from "../../../utils/sanitizeSortColumnName";

const SpinnerIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className="spin-icon"
    aria-hidden="true"
  >
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
);

function parseOptionalRowLimit(input) {
  const trimmed = String(input ?? "").trim();
  if (!trimmed) return null;

  const parsed = parseInt(trimmed, 10);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;

  return parsed;
}

/** @returns {{ kind: 'api' | 'query', max: number } | null} */
function resolveRowLimitWarning(parsed, prevParsed, rowLimit) {
  if (!Number.isFinite(parsed) || parsed <= 0) return null;

  if (parsed > EXECUTE_SQL_MAX_LIMIT) {
    if (prevParsed == null || prevParsed <= EXECUTE_SQL_MAX_LIMIT) {
      return { kind: "api", max: EXECUTE_SQL_MAX_LIMIT };
    }
    return null;
  }

  if (rowLimit != null && parsed > rowLimit) {
    if (prevParsed == null || prevParsed <= rowLimit) {
      return { kind: "query", max: rowLimit };
    }
  }

  return null;
}

function SortBatchLoadWarningContent({ batchRowLimit = EXECUTE_SQL_MAX_LIMIT }) {
  const limitLabel = batchRowLimit.toLocaleString();

  return (
    <>
      <p>
        Large datasets are loaded in batches to ensure a smooth and responsive
        experience.
      </p>
      <p>
        Currently displaying the <strong>{limitLabel}</strong> rows. Use{" "}
        <strong>Load More</strong> to continue.
      </p>
    </>
  );
}

function SortRowLimitWarningModal({ maxLimit, onClose }) {
  return createPortal(
    <div
      className="table-sort-limit-warning-overlay"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="table-sort-limit-warning-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="table-sort-limit-warning-title"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="table-sort-limit-warning-header">
          <div className="table-sort-limit-warning-header-title">
            <FontAwesomeIcon
              icon={faTriangleExclamation}
              className="table-sort-limit-warning-icon"
              aria-hidden="true"
            />
            <h5 id="table-sort-limit-warning-title">Row limit warning</h5>
          </div>
          <button
            type="button"
            className="table-sort-limit-warning-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="table-sort-limit-warning-body">
          <SortBatchLoadWarningContent batchRowLimit={maxLimit} />
        </div>
        <div className="table-sort-limit-warning-footer">
          <button
            type="button"
            className="table-sort-limit-warning-ok"
            onClick={onClose}
          >
            OK
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/**
 * Sort panel: sort order, optional row limit (<= query max, <= 1000 per request).
 * Values above the calculated row limit or above 1000 show a warning modal and reset on dismiss.
 */
export default function SqlResultColumnSortDropdown({
  columnName,
  isOpen,
  onApply,
  isLoading,
  defaultSortOrder = "asc",
  queryRowCount,
  loadedCount,
  totalRowCount: totalRowCountProp,
}) {
  const sortRequestMax =
    totalRowCountProp ?? resolveSortRowCount({ queryRowCount, loadedCount });
  const queryTotal = resolveQueryTotalRowCount({ queryRowCount, loadedCount });

  const [sortOrder, setSortOrder] = useState(defaultSortOrder);
  const [rowsInput, setRowsInput] = useState("");
  const [rowLimitWarning, setRowLimitWarning] = useState(null);
  const prevParsedRowsRef = useRef(null);

  const resetForm = () => {
    setSortOrder(defaultSortOrder);
    setRowsInput("");
    setRowLimitWarning(null);
    prevParsedRowsRef.current = null;
  };

  useEffect(() => {
    if (!isOpen) return;
    resetForm();
  }, [isOpen, columnName, defaultSortOrder]);

  if (!isOpen) return null;

  const handleRowsChange = (e) => {
    const digitsOnly = e.target.value.replace(/\D/g, "");
    if (digitsOnly === "") {
      setRowsInput("");
      prevParsedRowsRef.current = null;
      return;
    }

    const parsed = parseInt(digitsOnly, 10);
    setRowsInput(digitsOnly);

    const warning = resolveRowLimitWarning(
      parsed,
      prevParsedRowsRef.current,
      sortRequestMax
    );
    if (warning != null) {
      setRowLimitWarning({ ...warning, enteredValue: parsed });
    }
    prevParsedRowsRef.current = parsed;
  };

  const dismissRowLimitWarning = () => {
    setRowLimitWarning(null);
    setRowsInput("");
    prevParsedRowsRef.current = null;
  };

  const handleApply = () => {
    const limit = parseOptionalRowLimit(rowsInput);
    if (limit != null) {
      const warning = resolveRowLimitWarning(limit, null, sortRequestMax);
      if (warning != null) {
        setRowLimitWarning({ ...warning, enteredValue: limit });
        return;
      }
    }
    onApply?.({
      column_name: sanitizeSortColumnName(columnName),
      column_key: columnName,
      sort_order: sortOrder,
      ...(limit != null ? { limit } : {}),
    });
  };

  return (
    <>
      {rowLimitWarning != null ? (
        <SortRowLimitWarningModal
          maxLimit={rowLimitWarning.max}
          onClose={dismissRowLimitWarning}
        />
      ) : null}
      <div
        className="table-sort-dropdown-menu"
        role="dialog"
        aria-label="Sort options"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <fieldset className="table-sort-dropdown-section">
          <legend className="table-sort-dropdown-section-title">Sort order</legend>
          <label className="table-sort-dropdown-radio">
            <input
              type="radio"
              name={`sort-order-${columnName}`}
              value="asc"
              checked={sortOrder === "asc"}
              onChange={() => setSortOrder("asc")}
              disabled={isLoading}
            />
            Ascending
          </label>
          <label className="table-sort-dropdown-radio">
            <input
              type="radio"
              name={`sort-order-${columnName}`}
              value="desc"
              checked={sortOrder === "desc"}
              onChange={() => setSortOrder("desc")}
              disabled={isLoading}
            />
            Descending
          </label>
        </fieldset>

        {sortRequestMax != null ? (
          <fieldset className="table-sort-dropdown-section table-sort-dropdown-section--rows">
            <legend className="table-sort-dropdown-section-title">Rows to display</legend>
            <div className="table-sort-dropdown-rows-row">
              <input
                type="text"
                inputMode="numeric"
                className="table-sort-dropdown-rows-input"
                value={rowsInput}
                onChange={handleRowsChange}
                placeholder="Optional"
                disabled={isLoading}
                aria-label="Rows to display"
              />
              {queryTotal != null ? (
                <span className="table-sort-dropdown-rows-total">
                  / {queryTotal.toLocaleString()} Total Rows
                </span>
              ) : null}
            </div>
          </fieldset>
        ) : null}

        <div className="table-sort-dropdown-actions">
          <button
            type="button"
            className="table-sort-dropdown-reset"
            disabled={isLoading}
            onClick={resetForm}
          >
            Reset
          </button>
          <button
            type="button"
            className="table-sort-dropdown-apply"
            disabled={isLoading}
            onClick={handleApply}
          >
            {isLoading ? (
              <>
                <SpinnerIcon />
                Applying...
              </>
            ) : (
              "Apply"
            )}
          </button>
        </div>
      </div>
    </>
  );
}
